import { Box, Badge, Text, HStack } from '@chakra-ui/react'
import type { NutritionInfo, NutritionValues } from '@/types/recipe'

interface Props {
  nutrition: NutritionInfo
  variant: 'mobile' | 'desktop'
}

function formatG(val: number | null, decimals = 1): string {
  if (val === null) return '—'
  return `${val % 1 === 0 ? val : val.toFixed(decimals)}g`
}

function formatMg(val: number | null): string {
  if (val === null) return '—'
  return `${Math.round(val)}mg`
}

function formatSalt(sodiumMg: number | null): string {
  if (sodiumMg === null) return '—'
  const saltG = (sodiumMg / 1000) * 2.5
  return `${saltG < 0.1 ? '<0.1' : saltG.toFixed(1)}g`
}

interface CalculatedMeta {
  unanalysedIngredients: string[]
  servingsAssumed: boolean
}

function buildDisclaimerLines(meta: CalculatedMeta): string[] {
  const lines: string[] = []
  if (meta.unanalysedIngredients.length === 0) {
    lines.push('Nutrition calculated by Edamam.')
  } else {
    lines.push(`Nutrition calculated by Edamam — couldn't analyse: ${meta.unanalysedIngredients.join(', ')}.`)
  }
  if (meta.servingsAssumed) {
    lines.push('Per serving based on the lower end of a range given by the recipe.')
  }
  return lines
}

interface NutrientRow {
  label: string
  value: string
  sub?: boolean
}

function buildRows(n: NutritionValues): NutrientRow[] {
  return [
    { label: 'Energy', value: n.calories !== null ? `${Math.round(n.calories)} kcal` : '—' },
    { label: 'Fat', value: formatG(n.fat) },
    ...(n.saturatedFat !== null ? [{ label: 'of which saturates', value: formatG(n.saturatedFat), sub: true }] : []),
    { label: 'Carbohydrate', value: formatG(n.carbohydrate) },
    ...(n.sugar !== null ? [{ label: 'of which sugars', value: formatG(n.sugar), sub: true }] : []),
    { label: 'Fibre', value: formatG(n.fibre) },
    { label: 'Protein', value: formatG(n.protein) },
    { label: 'Salt', value: formatSalt(n.sodium) },
  ]
}

function MobilePanel({ n, calculated }: { n: NutritionValues; calculated?: CalculatedMeta }) {
  const rows = [
    ...buildRows(n),
    ...(n.cholesterol !== null ? [{ label: 'Cholesterol', value: formatMg(n.cholesterol), sub: false }] : []),
  ]
  const disclaimerLines = calculated ? buildDisclaimerLines(calculated) : null
  return (
    <Box px={4} pt={4} pb={4} data-testid="nutrition-panel">
      <Box border="1px solid" borderColor="border.muted" borderRadius="xl" overflow="hidden">
        <Box display="flex" justifyContent="space-between" alignItems="center" px={4} py="6px" bg="rgba(26,107,107,0.02)" borderBottomWidth="1px" borderColor="border.muted">
          <Box>
            {calculated && (
              <Badge variant="subtle" colorPalette="orange" fontSize="9px" letterSpacing="0.06em">
                Estimated
              </Badge>
            )}
          </Box>
          <Text fontSize="10px" color="text.muted" fontStyle="italic">
            per serving{n.servingSize ? ` (${n.servingSize})` : ''}
          </Text>
        </Box>
        {rows.map(({ label, value, sub }, idx) => (
          <Box
            key={label}
            display="flex"
            alignItems="center"
            px={4}
            py="10px"
            borderTopWidth={idx > 0 ? '1px' : '0'}
            borderColor="border.muted"
            bg={idx % 2 === 0 ? 'rgba(26,107,107,0.02)' : 'rgba(26,107,107,0.06)'}
            opacity={value === '—' ? 0.5 : 1}
          >
            <Text
              w="55%"
              fontSize="13px"
              fontWeight={sub ? '400' : '500'}
              color={sub ? 'text.subtle' : 'text.primary'}
              pl={sub ? 3 : 0}
              fontStyle={sub ? 'italic' : 'normal'}
            >
              {label}
            </Text>
            <Text w="45%" textAlign="right" fontSize="13px" fontWeight={sub ? '500' : '600'} color="text.primary">
              {value}
            </Text>
          </Box>
        ))}
      </Box>

      {disclaimerLines ? (
        <Box mt={2}>
          {disclaimerLines.map((line, i) => (
            <Text key={i} fontSize="11px" color="text.muted" textAlign="center" mt={i > 0 ? 1 : 0}>
              {line}
            </Text>
          ))}
        </Box>
      ) : (
        <Text fontSize="11px" color="text.muted" mt={2} textAlign="center">
          Nutritional info provided by source
        </Text>
      )}
    </Box>
  )
}

function DesktopPanel({ n, calculated }: { n: NutritionValues; calculated?: CalculatedMeta }) {
  const rows = buildRows(n)
  const disclaimerLines = calculated ? buildDisclaimerLines(calculated) : null
  return (
    <Box
      borderTopWidth="1px"
      borderColor="border.muted"
      mt={4}
      mx={-8}
      px={8}
      py={3}
      bg="bg.canvas"
    >
      <HStack justify="space-between" align="center" mb={2}>
        <HStack gap={2} align="center">
          <Text fontSize="9px" textTransform="uppercase" letterSpacing="0.12em" color="text.muted" fontWeight="600">
            Nutrition per serving{n.servingSize ? ` (${n.servingSize})` : ''}
          </Text>
          {calculated && (
            <Badge variant="subtle" colorPalette="orange" fontSize="9px" letterSpacing="0.06em">
              Estimated
            </Badge>
          )}
        </HStack>
        <Box textAlign="right">
          {disclaimerLines ? (
            disclaimerLines.map((line, i) => (
              <Text key={i} fontSize="9px" color="text.muted" mt={i > 0 ? '2px' : 0}>{line}</Text>
            ))
          ) : (
            <Text fontSize="9px" color="text.muted">Nutritional info provided by source</Text>
          )}
        </Box>
      </HStack>
      <HStack gap={0} align="stretch" overflowX="auto">
        {rows.map(({ label, value, sub }, idx) => (
          <Box
            key={label}
            flexShrink={0}
            pr={4}
            mr={4}
            borderRightWidth={idx < rows.length - 1 ? '1px' : '0'}
            borderColor="border.muted"
            opacity={value === '—' ? 0.45 : 1}
          >
            <Text
              fontSize="8px"
              textTransform="uppercase"
              letterSpacing="0.08em"
              color={sub ? 'text.subtle' : 'text.muted'}
              fontWeight="600"
              fontStyle={sub ? 'italic' : 'normal'}
            >
              {label}
            </Text>
            <Text fontSize="13px" fontWeight={sub ? '500' : '600'} color="text.primary" lineHeight="1.3">{value}</Text>
          </Box>
        ))}
      </HStack>
    </Box>
  )
}

export default function NutritionPanel({ nutrition, variant }: Props) {
  const n = nutrition.perServing
  if (!n) return null

  const calculated: CalculatedMeta | undefined =
    nutrition.source === 'calculated'
      ? {
          unanalysedIngredients: nutrition.unanalysedIngredients ?? [],
          servingsAssumed: nutrition.servingsAssumed ?? false,
        }
      : undefined

  if (variant === 'mobile') return <MobilePanel n={n} calculated={calculated} />
  return <DesktopPanel n={n} calculated={calculated} />
}
